// Pure helpers of the /display page (docs/display-page.md): metric sizes, rail prices by
// length and print + display set savings. Amounts are numbers, the page formats them.

const CM_PER_INCH = 2.54;

// A catalog product keeps its entry when the backend returns none of its SKUs, with empty
// prices. The page leaves such products out instead of showing them unpriced.
export const isPriced = (product) => Boolean(product?.prices?.length);

// One decimal, trailing .0 dropped, like the specs in the product content files ("61 cm").
export const inchesToCm = (inches) => String(Number((inches * CM_PER_INCH).toFixed(1)));

// "12 in (30.5 cm)", "36 or 48 in (91.4 or 121.9 cm)", "12 to 24 in (30.5 to 61 cm)". Two
// lengths read as a choice ("or") unless `range` asks for "to".
export const lengthLabel = (lengths, { range = false } = {}) => {
  const sorted = [...lengths].sort((a, b) => a - b);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (first === last) return `${first} in (${inchesToCm(first)} cm)`;
  const joiner = sorted.length === 2 && !range ? 'or' : 'to';
  return `${first} ${joiner} ${last} in (${inchesToCm(first)} ${joiner} ${inchesToCm(last)} cm)`;
};

// Rail SKUs are named wooden-rail-<inches>[-<finish>]. Legacy ids without a length are skipped.
export const railLength = (skuId) => {
  const match = /^wooden-rail-(\d+)(?:-|$)/.exec(skuId);
  return match ? Number(match[1]) : null;
};

// Cheapest finish per rail length, adjacent lengths at the same price merged:
// [{ lengths: [12, 14, 16, 17], price: 30 }, { lengths: [20], price: 35 }, ...].
export const railPriceGroups = (options) => {
  const byLength = new Map();
  options.forEach((option) => {
    const length = railLength(option.id);
    if (length == null) return;
    byLength.set(length, Math.min(byLength.get(length) ?? Infinity, option.price));
  });

  return [...byLength.entries()]
    .sort(([a], [b]) => a - b)
    .reduce((groups, [length, price]) => {
      const last = groups[groups.length - 1];
      if (last && last.price === price) {
        last.lengths.push(length);
      } else {
        groups.push({ lengths: [length], price });
      }
      return groups;
    }, []);
};

// Cheapest rail among the given lengths, null when none is sold.
export const railPriceFrom = (options, lengths) => {
  const prices = options
    .filter((option) => lengths.includes(railLength(option.id)))
    .map((option) => option.price);
  return prices.length ? Math.min(...prices) : null;
};

// A set against its parts bought separately, the comparison the PDP bundle picker shows
// (packages/ecommerce/src/components/productConfiguration.js): the first primary SKU plus the
// cheapest secondary SKU. `priceOf(skuId)` returns the SKU price or null. Savings are null
// unless the set is cheaper.
export const bundleComparison = (bundle, priceOf) => {
  const primary = priceOf(bundle.products.primary[0]);
  const secondary = bundle.products.secondary.map(priceOf).filter((price) => price != null);
  if (primary == null || !secondary.length) return null;

  const separately = primary + Math.min(...secondary);
  const savings = separately - bundle.price;
  return { separately, savings: savings > 0 ? savings : null };
};
